import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../../app/store";
import {
	LOCAL_STORAGE_TOKEN_ADMIN_CMS,
	LOCAL_STORAGE_TOKEN_KEY,
	LOCAL_STORAGE_USER_PHONE_NUMBER,
	USER_INFO,
} from "../../../../configs/auth";
import authPortal from "../../../../apis/portal/auth";

type AuthState = {
	auth: {};
	isLoading: boolean;
	otp: string;
};

const initialState: AuthState = {
	auth: {},
	isLoading: false,
	otp: "",
};

export const getOtp = createAsyncThunk(
	"auth/getOtp",
	async (phoneNumber: string, thunkApi) => {
		try {
			const response = await authPortal.getOtp(phoneNumber);
			return response.data;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const getToken = createAsyncThunk(
	"auth/getToken",
	async (
		{ phoneNumber, otp, ip }: { phoneNumber: string; otp: string; ip: string },
		thunkApi
	) => {
		try {
			const response = await authPortal.getToken(phoneNumber, otp, ip);
			return response.data;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const getLoginSSO = createAsyncThunk(
	"auth/login-sso",
	async (
		{ username, password }: { username: string; password: string },
		thunkApi
	) => {
		try {
			const response = await authPortal.getLoginSSO(username, password);
			return response.data;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const loginByHc = createAsyncThunk(
	"auth/loginByHc",
	async (phone: string, thunkApi) => {
		try {
			const response = await authPortal.loginByHc(phone);
			return response.data;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const loginCasSso = createAsyncThunk(
	"auth/cas-sso",
	async (ticket: string, thunkApi) => {
		try {
			const response = await authPortal.validateCasTicket(ticket);
			return response.data;
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

const auth = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setPhoneNumber: (state, action) => {
			localStorage.setItem(LOCAL_STORAGE_USER_PHONE_NUMBER, action.payload);
		},
		setUsers: (state, action) => {
			state.auth = action.payload;
			localStorage.setItem(USER_INFO, JSON.stringify(action.payload));
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getOtp.pending, (state) => {
				return state;
			})
			.addCase(getOtp.fulfilled, (state, { payload }: any) => {
				state.otp = payload.data.payload?.otp;
				return state;
			})
			.addCase(getOtp.rejected, (state) => {
				return state;
			})
			.addCase(getToken.pending, (state) => {
				return state;
			})
			.addCase(getToken.fulfilled, (state, { payload }: any) => {
				localStorage.setItem(
					LOCAL_STORAGE_TOKEN_KEY,
					payload.data.payload?.token
				);
				return state;
			})
			.addCase(getToken.rejected, (state) => {
				return state;
			})
			.addCase(getLoginSSO.pending, (state) => {
				return state;
			})
			.addCase(getLoginSSO.fulfilled, (state, { payload }: any) => {
				localStorage.setItem(
					LOCAL_STORAGE_TOKEN_KEY,
					payload.data.payload?.token
				);
				return state;
			})
			.addCase(getLoginSSO.rejected, (state) => {
				return state;
			})
			.addCase(loginByHc.pending, (state) => {
				return state;
			})
			.addCase(loginByHc.fulfilled, (state, { payload }: any) => {
				localStorage.setItem(
					LOCAL_STORAGE_USER_PHONE_NUMBER,
					payload.data.payload?.phone
				);
				localStorage.setItem(
					LOCAL_STORAGE_TOKEN_KEY,
					payload.data.payload?.token
				);
				return state;
			})
			.addCase(loginByHc.rejected, (state) => {
				return state;
			})
			.addCase(loginCasSso.pending, (state) => {
				return state;
			})
			.addCase(loginCasSso.fulfilled, (state, { payload }: any) => {
				if (payload?.token) {
					localStorage.setItem(
						LOCAL_STORAGE_TOKEN_KEY,
						payload.token
					);
				}
				return state;
			})
			.addCase(loginCasSso.rejected, (state) => {
				return state;
			});
	},
});

export const dataAuth = (state: RootState) => state;
export const { setPhoneNumber, setUsers } = auth.actions;
export default auth.reducer;
